<script setup>
import { computed } from 'vue'
import AppMarkdown from '@/components/atoms/AppMarkdown'
import HelpHubHeading from './HelpHubHeading.vue'
import HelpHubTip from './HelpHubTip.vue'

const props = defineProps({
  title: { type: String, required: true },
  // The copy, in Markdown (bold, italic, links); a section can also be a heading over a list alone.
  text: { type: String, default: '' },
  // Words of the text that are a link: `{ text, href }`.
  link: { type: Object, default: null },
  // Numbered steps, and plain points, under the copy.
  list: { type: Array, default: () => [] },
  bullets: { type: Array, default: () => [] },
  // A picture under the copy: `{ src, alt, caption }`; one without alt text is decorative.
  image: { type: Object, default: null },
  // A 12px heading: Figma's H4, a point under a section.
  compact: { type: Boolean, default: false },
  // A help page the heading opens.
  to: { type: [String, Object], default: null },
  // Advice under the copy, with its heading.
  tip: { type: String, default: '' },
  tipTitle: { type: String, default: '' },
})

// The copy on either side of the linked words.
const parts = computed(() => (props.link ? props.text.split(props.link.text) : [props.text]))
</script>

<template>
  <section class="flex flex-col gap-y-2">
    <HelpHubHeading :title="title" :to="to" :compact="compact" />

    <!-- The copy and a list under it are one text node in Figma, so they run on without a gap. -->
    <div v-if="text || list.length || bullets.length" class="text-xs text-gray-600">
      <!-- Markdown turns each line break into <br>, so a blank line in the copy stays a paragraph break. -->
      <p v-if="text">
        <AppMarkdown :source="parts[0]" inline /><a v-if="link" :href="link.href" target="_blank" rel="noopener" class="tinted-link font-semibold" v-text="link.text" /><AppMarkdown v-if="link" :source="parts[1]" inline />
      </p>

      <!-- Markers sit outside, so a wrapped line hangs under the text; pl-5 leaves room for a two-digit number. -->
      <ol v-if="list.length" class="list-decimal pl-5">
        <li v-for="line in list" :key="line" v-text="line" />
      </ol>

      <ul v-if="bullets.length" class="list-disc pl-5">
        <li v-for="line in bullets" :key="line" v-text="line" />
      </ul>
    </div>

    <figure v-if="image?.src" class="flex flex-col gap-y-1.5">
      <img :src="image.src" :alt="image.alt ?? ''" loading="lazy" class="w-full rounded-xl border border-gray-200" />
      <figcaption v-if="image.caption" class="text-xs text-gray-500" v-text="image.caption" />
    </figure>

    <!-- Figma sets the tip 12px under the copy. -->
    <HelpHubTip v-if="tip" :title="tipTitle" compact class="mt-1">{{ tip }}</HelpHubTip>
  </section>
</template>
